export default class HandleVideoEmbeds {
	constructor(options = {}) {
		this.containerSel = options.containerSel;
		this.iframeElement = options.iframeElement; // Iframe element already created
		this.coverSel = options.coverSelector;
		this.callbacks = {
			onReady: options.onReady || (() => {}),
			onPlay: options.onPlay || (() => {}),
			onPause: options.onPause || (() => {}),
			onEnd: options.onEnd || (() => {}),
			onError: options.onError || (() => {}),
		};

		this.player = null;
		this.isPlaying = false;
		this.isLoaded = false;
		this.originalIframeSrc = this.iframeElement ? this.iframeElement.src : ''; // Store original URL
		this.iframeAttributes = {}; // To store attributes of the original iframe

		this.init();
	}

	init() {
		this.containerElement = this.iframeElement.closest(this.containerSel);

		if (!this.containerElement) {
			console.error(`Container element '${this.containerSel}' not found`);
			return;
		}

		if (!this.iframeElement) {
			console.error("No iframe element provided");
			return;
		}

		// Save original iframe attributes for later recreation
		this.saveIframeAttributes();

		// Set up the cover element
		this.coverElement = this.containerElement.querySelector(this.coverSel);

		// Initialize the corresponding API
		if (this.isYoutubeEmbed()) {
			this.loadYouTubeAPI();
		} else if (this.isVimeoEmbed()) {
			this.loadVimeoAPI();
		} else {
			console.error(`Platform not supported`);
		}
	}

	// Save all attributes of the original iframe
	saveIframeAttributes() {
		if (!this.iframeElement) return;

		// Save dimensions and other important attributes
		this.iframeAttributes = {
			width: this.iframeElement.width || '640',
			height: this.iframeElement.height || '360',
			allow: this.iframeElement.allow || 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture',
			allowFullscreen: this.iframeElement.allowFullscreen,
			frameBorder: this.iframeElement.frameBorder || '0',
			id: this.iframeElement.id || '',
			className: this.iframeElement.className || '',
			style: this.iframeElement.getAttribute('style') || ''
		};
	}

	showPlayer() {
		const coverElement = this.coverElement;

		if (coverElement) {
			coverElement.classList.add('hide');
		}

		// If the player hasn't been initialized yet, initialize it
		if (!this.isLoaded) {
			if (this.isYoutubeEmbed() && window.YT) {
				this.initYouTubePlayer();
			} else if (this.isVimeoEmbed() && window.Vimeo) {
				this.initVimeoPlayer();
			}
		}
	}

	showCover() {
		const coverElement = this.coverElement;

		if (coverElement) {
			coverElement.classList.remove('hide');
		}
	}

	loadYouTubeAPI() {
		if (window.YT && window.YT.Player) {
			this.initYouTubePlayer();
			return;
		}

		// Global callback for when the YouTube API is ready
		window.onYouTubeIframeAPIReady = () => {
			// Check if the YouTube API is actually available
			if (window.YT && window.YT.Player) {
				// Initialize all YouTube players that are waiting
				if (!this.isLoaded && this.isYoutubeEmbed()) {
					this.initYouTubePlayer();
				}
			} else {
				console.error('YouTube API was not loaded correctly');
			}
		};

		// Load YouTube API script if not already loaded
		if (!document.getElementById("youtube-api")) {
			const tag = document.createElement("script");
			tag.id = "youtube-api";
			tag.src = "https://www.youtube.com/iframe_api";
			const firstScriptTag = document.getElementsByTagName("script")[0];
			firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
		}
	}

	initYouTubePlayer() {
		const iframe = this.iframeElement;

		if (!iframe) {
			console.error(`YouTube iframe element not found`);
			return;
		}

		// Check if the iframe has the enablejsapi parameter
		try {
			// Make sure JavaScript API is enabled in the iframe
			let url = new URL(iframe.src);

			// Check if the parameter already exists to avoid duplication
			if (!url.searchParams.has('enablejsapi')) {
				url.searchParams.set('enablejsapi', '1');
				iframe.src = url.toString();
			}

			// IMPORTANT: To avoid problems with the onStateChange event,
			// make sure the origin is set correctly
			if (!url.searchParams.has('origin')) {
				url.searchParams.set('origin', window.location.origin);
				iframe.src = url.toString();
			}
		} catch (e) {
			console.warn('Error modifying iframe URL:', e);
		}

		// Initialize the YouTube player with the existing iframe
		try {
			this.player = new YT.Player(iframe, {
				events: {
					onReady: (event) => {
						this.isLoaded = true;
						this.callbacks.onReady(event);
					},
					onStateChange: (event) => {
						// YT.PlayerState: UNSTARTED(-1), ENDED(0), PLAYING(1), PAUSED(2), BUFFERING(3), CUED(5)
						if (event.data === YT.PlayerState.PLAYING) {
							this.isPlaying = true;
							this.callbacks.onPlay(event);
						} else if (event.data === YT.PlayerState.PAUSED) {
							this.isPlaying = false;
							this.callbacks.onPause(event);
						} else if (event.data === YT.PlayerState.ENDED) {
							this.isPlaying = false;
							this.callbacks.onEnd(event);

							if (this.coverElement) {
								this.showCover();
							}
						} else if (event.data === -1) {
							// console.log('Video not started (UNSTARTED)');
						} else if (event.data === 3) {
							// console.log('Video loading (BUFFERING)');
						} else if (event.data === 5) {
							// console.log('Video in queue (CUED)');
						}
					},
					onError: (event) => {
						console.error('YouTube player error:', event.data);
						this.callbacks.onError(event);
					},
				},
				// Disable autoplay to ensure events are triggered correctly
				playerVars: {
					autoplay: 0,
					controls: 1,
					rel: 0
				}
			});
		} catch (e) {
			console.error('Error initializing YouTube player:', e);
		}
	}

	loadVimeoAPI() {
		if (window.Vimeo) {
			this.initVimeoPlayer();
			return;
		}

		// Load Vimeo API script if not already loaded
		if (!document.getElementById("vimeo-api")) {
			const tag = document.createElement("script");
			tag.id = "vimeo-api";
			tag.src = "https://player.vimeo.com/api/player.js";
			tag.onload = () => {
				if (!this.isLoaded && this.isVimeoEmbed()) {
					this.initVimeoPlayer();
				}
			};
			const firstScriptTag = document.getElementsByTagName("script")[0];
			firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
		}
	}

	initVimeoPlayer() {
		const iframe = this.iframeElement;

		if (!iframe) {
			console.error(`Vimeo iframe element not found`);
			return;
		}

		// Initialize the Vimeo player with the existing iframe
		this.player = new Vimeo.Player(iframe);
		this.isLoaded = true;

		this.play();

		this.player.on("play", () => {
			this.isPlaying = true;
			this.callbacks.onPlay();
		});

		this.player.on("pause", () => {
			this.isPlaying = false;
			this.callbacks.onPause();
		});

		this.player.on("ended", () => {
			this.isPlaying = false;
			this.callbacks.onEnd();

			// Show the cover again if it exists
			if (this.coverElement) {
				this.showCover();
			}
		});

		this.player.on("error", (error) => {
			this.callbacks.onError(error);
		});
	}

	isYoutubeEmbed() {
		return this.iframeElement.src.indexOf('youtube.com') >= 0;
	}

	isVimeoEmbed() {
		return this.iframeElement.src.indexOf('vimeo.com') >= 0;
	}

	// Public methods to control the player
	play() {
		if (!this.player) return;

		if (this.isYoutubeEmbed()) {
			typeof(this.player.playVideo) === 'function' && this.player.playVideo();
		} else if (this.isVimeoEmbed()) {
			this.player.play();
		}

		// Show the player if it's hidden
		this.showPlayer();
	}

	pause() {
		if (!this.player) return;

		if (this.isYoutubeEmbed()) {
			typeof(this.player.pauseVideo) === 'function' && this.player.pauseVideo();
		} else if (this.isVimeoEmbed()) {
			this.player.pause();
		}
	}

	stop() {
		if (!this.player) return;

		if (this.isYoutubeEmbed()) {
			this.player.stopVideo();
		} else if (this.isVimeoEmbed()) {
			this.player.pause().then(() => {
				this.player.setCurrentTime(0);
			});
		}

		// Show the cover again if it exists
		if (this.coverElement) {
			this.showCover();
		}
	}

	seekTo(seconds) {
		if (!this.player) return;

		if (this.isYoutubeEmbed()) {
			this.player.seekTo(seconds, true);
		} else if (this.isVimeoEmbed()) {
			this.player.setCurrentTime(seconds);
		}
	}

	setVolume(volume) {
		// volume: 0 to 100 for YouTube, 0 to 1 for Vimeo
		if (!this.player) return;

		if (this.isYoutubeEmbed()) {
			this.player.setVolume(volume);
		} else if (this.isVimeoEmbed()) {
			this.player.setVolume(volume / 100);
		}
	}

	setMute(mute) {
		if (!this.player) return;

		if (this.isYoutubeEmbed()) {
			if (mute) {
				this.player.mute();
			} else {
				this.player.unMute();
			}
		} else if (this.isVimeoEmbed()) {
			this.player.setVolume(mute ? 0 : 1);
		}
	}

	// Destroy the player and clean up resources
	destroy() {
		if (this.player) {
			if (this.isYoutubeEmbed()) {
				// Remove event listeners first
				if (typeof this.player.removeEventListener === 'function') {
					try {
						this.player.removeEventListener('onReady');
						this.player.removeEventListener('onStateChange');
						this.player.removeEventListener('onError');
					} catch (e) {
						console.warn('Could not remove event listeners:', e);
					}
				}

				// Stop the video first
				try {
					this.player.stopVideo();
				} catch (e) {
					console.warn('Error stopping the video:', e);
				}

				// Destroy the player - IMPORTANT: This will remove the iframe from the DOM
				try {
					this.player.destroy();
				} catch (e) {
					console.error('Error destroying the player:', e);
				}

				// Recreate the iframe after player destruction
				this.reAppendIframe();
			} else if (this.isVimeoEmbed()) {
				try {
					this.player.unload();
				} catch (e) {
					console.error('Error unloading the Vimeo player:', e);
				}
			}

			this.player = null;
			this.isLoaded = false;
			this.isPlaying = false;
		}

		// Show the cover again if it exists
		if (this.coverElement) {
			this.coverElement.classList.remove('hide');
		}
	}

	reAppendIframe() {
		// Get the container where the iframe should be
		const container = this.coverElement || this.containerElement;
		if (!container) {
			console.error('Container for iframe not found');
			return;
		}

		// Create a new iframe with the same attributes as the original
		const newIframe = document.createElement('iframe');

		// Set basic attributes for the new iframe
		newIframe.src = this.originalIframeSrc;

		// Apply saved attributes from the original iframe
		Object.keys(this.iframeAttributes).forEach(attr => {
			if (this.iframeAttributes[attr]) {
				if (attr === 'allowFullscreen') {
					newIframe.setAttribute('allowfullscreen', '');
				} else if (attr === 'style') {
					newIframe.setAttribute('style', this.iframeAttributes[attr]);
				} else if (attr === 'className') {
					newIframe.className = this.iframeAttributes[attr];
				} else {
					newIframe[attr] = this.iframeAttributes[attr];
				}
			}
		});

		newIframe.style = "opacity: 1; visibility: inherit;";

		// Make sure the iframe has an ID for future reference
		if (!newIframe.id && this.iframeAttributes.id) {
			newIframe.id = this.iframeAttributes.id;
		}

		// Add the new iframe to the container
		if (container.firstChild) {
			container.insertBefore(newIframe, container.firstChild);
		} else {
			container.appendChild(newIframe);
		}

		// Update the iframe reference
		this.iframeElement = newIframe;
	}
}
